export function openExternalUrl(url: string) {
  window.open(url, '_blank', 'noopener,noreferrer');
}

type DeferredExternalTab = {
  /** Redirect the pre-opened tab to the resolved URL. */
  resolve: (url: string) => void;
  /** Close the pre-opened tab (e.g. when the async work fails). */
  cancel: () => void;
};

/**
 * Open a blank tab *synchronously* within a user-gesture handler, before the
 * destination URL is known, then redirect it once an async call resolves.
 *
 * Safari and Firefox block `window.open` that runs after an `await` (e.g. in a
 * mutation's `onSuccess`) because the click's transient user activation has
 * already been consumed by the time the promise settles. Opening the tab during
 * the gesture and navigating it afterwards keeps the popup blocker happy.
 *
 * Call this at the top of the click handler, then `resolve` on success and
 * `cancel` on error.
 */
export function openDeferredExternalUrl(): DeferredExternalTab {
  const newWindow = window.open('about:blank', '_blank');

  // We can't pass `noopener` to `window.open` here — it nulls out the reference
  // we need to redirect the tab — so sever the opener link manually to guard
  // against reverse tabnabbing. `about:blank` is same-origin, so this is allowed.
  if (newWindow) {
    newWindow.opener = null;
  }

  return {
    resolve(url: string) {
      if (newWindow && !newWindow.closed) {
        newWindow.location.href = url;
      } else {
        // The blank tab was blocked too; fall back to navigating the current tab.
        globalThis.location.href = url;
      }
    },
    cancel() {
      newWindow?.close();
    },
  };
}
